// Story 7.18 (ADO-1387), AC1 — capability detection for the scan control.
//
// Deliberately library-free (no `barcode-detector` import) so the header trigger
// and this gate stay cheap and unit-testable without pulling the wasm decoder into
// the bundle graph of a test that only checks whether the control shows.
//
// The DECODER is always available (the `barcode-detector` ponyfill falls back to
// zxing-wasm where the native API is absent — see `barcodeDecoder.ts`), so the only
// thing that can be missing is the CAMERA: `navigator.mediaDevices.getUserMedia`.
// It is absent in an insecure context (getUserMedia requires HTTPS/localhost) and
// on browsers without the API. Per AC1 the control is then rendered DISABLED WITH A
// STATED REASON (PO decision 2026-08-05), never an enabled no-op, and text search
// stays fully usable.
//
// A camera that EXISTS but denies permission or has no device is NOT gated here — the
// API is present, so the control is enabled; those cases surface as the AC7 recovery
// messages once the user opens the scanner (permission-denied / no-camera), which is
// a real action, not a no-op.

export interface ScanCapability {
  /** True when a camera capture can be attempted (the API is present). */
  readonly available: boolean;
  /** When unavailable, a short user-facing reason for the disabled control (AC1). */
  readonly reason?: string;
}

/** The reason shown on the disabled scan control when no camera API is present. */
export const NO_CAMERA_REASON = 'Scanning needs a camera on this device';

/** Detect whether a barcode scan can be attempted on this device (AC1). */
export function detectScanCapability(): ScanCapability {
  const hasCameraApi =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getUserMedia === 'function';
  return hasCameraApi ? { available: true } : { available: false, reason: NO_CAMERA_REASON };
}
